import styled from '@emotion/styled';
import { ClaimIdBadge } from '../../badge';
import { Variables } from '../../base';

interface INote {
    note?: boolean;
    aiNote?: boolean;
}

/** Only the Header has padding; inner sections have none to avoid stacking. */
const noteHeader = `
    padding: 16px 32px;
    gap: 32px;
    background: linear-gradient(180deg, ${Variables.ThemeLight.NeutralMild2} 0%, ${Variables.ThemeLight.Background2} 100%);
`;
const aiNoteHeader = `
    padding: 16px 32px;
    gap: 32px;
    background: linear-gradient(180deg, ${Variables.ThemeLight.PrimaryMild2} 0%, ${Variables.ThemeLight.Background2} 100%);
`;

const commentHeader = `
    padding: 16px 32px;
    gap: 32px;
    color: ${Variables.Colors.n85};
    background-color: ${Variables.ThemeLight.Background2};
`;

export const Header = styled.div<INote>`
    display: flex;
    flex-direction: column;
    ${(props: INote): string | undefined => (props.aiNote ? aiNoteHeader : props.note ? noteHeader : commentHeader)}
`;

export const Row = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;
export const MessageGrid = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

export const Subject = styled.div`
    font-weight: ${Variables.FontWeight.semibold};
    font-size: ${Variables.FontSizes.large};
    leading-trim: NONE;
    line-height: 100%;
    letter-spacing: 0%;
    color: ${Variables.ThemeLight.TextStrong};
    cursor: pointer;
`;

export const EditableSubject = styled.div`
    cursor: pointer;
`;

export const ClaimButton = styled(ClaimIdBadge)`
    margin-right: 8px;
    margin-bottom: 8px;
    align-self: baseline;
`;

export const Body = styled.div`
    font-weight: ${Variables.FontWeight.regular};
    font-size: ${Variables.FontSizes.tiny};
    leading-trim: NONE;
    line-height: 100%;
    letter-spacing: 0%;
    color: ${Variables.ThemeLight.TextMedium};

    pre,
    code {
        white-space: pre-wrap;
        word-wrap: break-word;
        overflow-wrap: break-word;
    }
`;

export const Attachments = styled.div``;

export const LoadingText = styled.div`
    font-size: 14px;
    color: ${Variables.Colors.n50};
    font-style: italic;
    padding: 8px 0;
`;

export const MetaSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
`;

/** Wrapper table: Claims and Codings as collapsible rows in one table. */
export const SectionsTable = styled.div`
    width: 100%;
    border: 1px solid ${Variables.ThemeLight.Border3};
    border-radius: 4px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
`;

export const SectionRow = styled.div`
    &:not(:last-child) {
        border-bottom: 1px solid ${Variables.ThemeLight.Border2};
    }
`;

// Codings table styles
export const CodingsTable = styled.table`
    width: 100%;
    background-color: ${Variables.Colors.white};
    border: 1px solid ${Variables.ThemeLight.Border2};
    border-radius: 4px;
    overflow: hidden;
    border-spacing: 0px;

    thead th {
        border-bottom: 1px solid ${Variables.ThemeLight.Border2};
    }
    tbody tr:not(:last-child) td {
        border-bottom: 1px solid ${Variables.ThemeLight.Border2};
    }

    thead tr:first-of-type th:first-of-type {
        border-top-left-radius: 4px;
    }
    thead tr:first-of-type th:last-of-type {
        border-top-right-radius: 4px;
    }
    tbody tr:last-child td:first-of-type {
        border-bottom-left-radius: 4px;
    }
    tbody tr:last-child td:last-of-type {
        border-bottom-right-radius: 4px;
    }
`;

export const CodingsTableHead = styled.thead`
    background-color: ${Variables.ThemeLight.Background1};
`;

export const CodingsTableBody = styled.tbody`
    background-color: ${Variables.ThemeLight.Background2};
    overflow: visible;
`;

export const CodingsTableRow = styled.tr`
    &:not(:last-child) {
    }

    &:hover {
        background-color: ${Variables.Colors.n5};
    }
`;

export const CodingsTableHeaderCell = styled.th`
    text-align: left;
    padding: 12px;
    font-size: 14px;
    font-weight: ${Variables.FontWeight.medium};
    color: ${Variables.ThemeLight.TextMedium};
    letter-spacing: 0.5px;
`;

export const CodingsTableCell = styled.td`
    padding: 12px;
    font-size: 14px;
    color: ${Variables.ThemeLight.TextMedium};
    font-weight: ${Variables.FontWeight.regular};
    vertical-align: middle;
`;

export const NotesCell = styled(CodingsTableCell)`
    max-width: 500px;
    white-space: pre-wrap;
    word-break: break-word;
`;

export const InfoButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 4px 8px;
    background-color: transparent;
    border: 1px solid ${Variables.Colors.b200};
    border-radius: 4px;
    color: ${Variables.Colors.b300};
    cursor: pointer;
    transition: all 0.2s ease;
    font-size: 12px;

    &:hover {
        background-color: ${Variables.Colors.b50};
        border-color: ${Variables.Colors.b300};
    }

    &:active {
        transform: scale(0.95);
    }
`;

export const EmptyMessage = styled.div`
    text-align: center;
    padding: 32px;
    color: ${Variables.Colors.n50};
    font-size: 14px;
`;
